import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Upload, Wand2, X } from "lucide-react";
import { toast } from "sonner";
import {
  deleteCustomPage,
  fetchCustomPages,
  fetchPurchases,
  saveCustomPage,
} from "@/lib/api";
import { fileToLineArt } from "@/lib/lineArt";
import PaywallModal from "@/components/PaywallModal";
import { sfx } from "@/lib/sounds";

function fitToCanvas(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = 800;
      c.height = 1000;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#FFFDF8";
      ctx.fillRect(0, 0, 800, 1000);
      const r = Math.min(800 / img.width, 1000 / img.height);
      const w = img.width * r;
      const h = img.height * r;
      ctx.drawImage(img, (800 - w) / 2, (1000 - h) / 2, w, h);
      URL.revokeObjectURL(url);
      resolve(c.toDataURL("image/jpeg", 0.9));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("bad image"));
    };
    img.src = url;
  });
}

export default function MyPages() {
  const [pages, setPages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const lineArtRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [paywall, setPaywall] = useState(false);
  const [converting, setConverting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCustomPages()
      .then(setPages)
      .catch(() => {});
    fetchPurchases()
      .then((p) => setProducts(p.products))
      .catch(() => {});
  }, []);

  const hasAutoLine = products.includes("autoline");

  const onLineArtFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setConverting(true);
    try {
      const image = await fileToLineArt(file);
      const item = await saveCustomPage({ title: "My Line Art", image });
      setPages((p) => [item, ...p]);
      sfx.sparkle();
      toast.success("Abraca-doodle! Your photo is now a coloring page!");
    } catch (err) {
      toast.error("Hmm, that photo resisted the spell — try another");
    }
    setConverting(false);
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const image = await fitToCanvas(file);
      const title =
        file.name.replace(/\.[^.]+$/, "").slice(0, 40) || "My Page";
      const item = await saveCustomPage({ title, image });
      setPages((p) => [item, ...p]);
      sfx.sparkle();
      toast.success("Page added to My Pages!");
    } catch (err) {
      toast.error("That image didn't work — try another one");
    }
    setUploading(false);
  };

  return (
    <div className="mb-16" data-testid="my-pages-section">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="font-display text-3xl text-slime text-glow-slime sm:text-4xl">
              My Pages
            </h3>
            <span className="rounded-full border border-slime/50 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-slime">
              Your Uploads
            </span>
          </div>
          <p className="mt-1 text-sm text-paper/60">
            Add your own coloring pictures — line art with closed shapes fills
            best with the magic bucket.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4">
        <motion.button
          data-testid="lineart-card"
          onClick={() => {
            sfx.click();
            if (hasAutoLine) lineArtRef.current.click();
            else setPaywall(true);
          }}
          whileHover={{ y: -6 }}
          className={`card-edge flex min-h-[220px] flex-col items-center justify-center gap-3 border-2 px-4 text-center transition-all ${
            hasAutoLine
              ? "border-slime/50 hover:border-slime hover:shadow-glow-slime"
              : "border-pumpkin/50 hover:border-pumpkin hover:shadow-glow-orange"
          }`}
        >
          <span
            className={`rounded-full p-4 ${hasAutoLine ? "bg-slime/15 text-slime" : "bg-pumpkin/15 text-pumpkin"}`}
          >
            <Camera size={28} />
          </span>
          <span className="font-semibold text-paper">
            {converting ? "Casting the spell..." : "Photo to Line Art"}
          </span>
          <span className="text-xs text-paper/50">
            {hasAutoLine ? "Any photo becomes a coloring page" : "Magic premium tool — unlock for $10"}
          </span>
        </motion.button>

        <motion.button
          data-testid="upload-card"
          onClick={() => {
            sfx.click();
            inputRef.current.click();
          }}
          whileHover={{ y: -6 }}
          className={`card-edge flex min-h-[220px] flex-col items-center justify-center gap-3 border-2 border-dashed border-pumpkin/50 px-4 text-center transition-all hover:border-pumpkin hover:shadow-glow-orange ${
            uploading ? "animate-pulse opacity-60" : ""
          }`}
        >
          <span className="rounded-full bg-pumpkin/15 p-4 text-pumpkin">
            <Upload size={28} />
          </span>
          <span className="font-semibold text-paper">
            {uploading ? "Brewing..." : "Upload a coloring page"}
          </span>
          <span className="text-xs text-paper/50">
            PNG, JPG or SVG from your device
          </span>
        </motion.button>

        {pages.map((p, i) => (
          <motion.div
            key={p.id}
            data-testid={`my-page-card-${p.id}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            whileHover={{ y: -6, rotate: i % 2 ? 0.6 : -0.6 }}
            onClick={() => {
              sfx.boop();
              navigate(`/color/custom-${p.id}`);
            }}
            className="card-edge group relative cursor-pointer p-4 transition-all hover:border-slime/50 hover:shadow-glow-slime"
          >
            <button
              data-testid={`my-page-delete-${p.id}`}
              aria-label="Delete uploaded page"
              onClick={async (e) => {
                e.stopPropagation();
                sfx.pop();
                setPages((list) => list.filter((x) => x.id !== p.id));
                try {
                  await deleteCustomPage(p.id);
                } catch (err) {}
              }}
              className="absolute right-2 top-2 z-10 rounded-full border border-white/20 bg-ink/80 p-1.5 text-paper/70 opacity-0 transition-all hover:border-candy hover:text-candy group-hover:opacity-100"
            >
              <X size={13} />
            </button>
            <div className="overflow-hidden rounded-2xl bg-[#FFFDF8] p-2">
              <img src={p.image} alt={p.title} className="w-full" />
            </div>
            <div className="mt-3 flex items-center justify-between px-1">
              <span className="truncate text-sm font-semibold text-paper">
                {p.title}
              </span>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slime text-ink">
                <Wand2 size={15} />
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      <input
        ref={inputRef}
        data-testid="upload-input"
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
      />
      <input
        ref={lineArtRef}
        data-testid="lineart-input"
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onLineArtFile}
      />
      {paywall && <PaywallModal owned={products} onClose={() => setPaywall(false)} />}
    </div>
  );
}
